import React, { useContext } from 'react';
import { AppContext } from '../../App';
import { 
  ComposedChart, 
  Bar, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  Cell,
  ReferenceLine
} from 'recharts';
import { Activity } from 'lucide-react';

const MarketPulse = () => {
  const { dashboardData } = useContext(AppContext);
  
  if (!dashboardData) return null;
  
  const { trend_data } = dashboardData;
  
  // Process data for net change visualization
  const pulseData = trend_data.slice(-14).map(item => {
    const netChange = item.vehicles_added - item.vehicles_sold;
    return {
      date: item.date.slice(5),
      added: item.vehicles_added,
      sold: item.vehicles_sold,
      netChange,
      price: item.avg_price,
      priceNormalized: ((item.avg_price - 33000) / 5000) * 100 // Normalize for visibility
    };
  });
  
  // Calculate market health score
  const avgNetChange = pulseData.reduce((acc, d) => acc + d.netChange, 0) / pulseData.length;
  const healthScore = Math.min(100, Math.max(0, 50 + (avgNetChange / 50)));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      return (
        <div className="glass p-4 rounded-lg min-w-[200px]">
          <p className="text-white text-sm font-bold mb-3 border-b border-[#27272A] pb-2">{label}</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded bg-[#22C55E]" />
              <span className="text-[#A1A1AA]">Added:</span>
            </div>
            <span className="text-white font-mono text-right">{data?.added?.toLocaleString()}</span>
            
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded bg-[#EF4444]" />
              <span className="text-[#A1A1AA]">Sold:</span>
            </div>
            <span className="text-white font-mono text-right">{data?.sold?.toLocaleString()}</span>
            
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded bg-[#3B82F6]" />
              <span className="text-[#A1A1AA]">Net:</span>
            </div>
            <span className={`font-mono text-right ${data?.netChange >= 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
              {data?.netChange >= 0 ? '+' : ''}{data?.netChange?.toLocaleString()}
            </span>
            
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded bg-[#A855F7]" />
              <span className="text-[#A1A1AA]">Avg Price:</span>
            </div>
            <span className="text-white font-mono text-right">${data?.price?.toLocaleString()}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dashboard-card p-6" data-testid="market-pulse">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#3B82F6]/10 rounded-lg">
            <Activity className="w-5 h-5 text-[#3B82F6]" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Market Pulse</h3>
            <p className="text-sm text-[#A1A1AA]">14-day inventory flow &amp; pricing momentum</p>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          {/* Legend */}
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-[#22C55E] rounded" />
              <span className="text-[#A1A1AA]">Added</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-[#EF4444] rounded" />
              <span className="text-[#A1A1AA]">Sold</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-2 bg-[#A855F7]" />
              <span className="text-[#A1A1AA]">Price Trend</span>
            </div>
          </div>
          
          {/* Health Score */}
          <div className="flex items-center gap-3 px-4 py-2 bg-[#27272A] rounded-lg">
            <span className="text-xs text-[#A1A1AA]">Market Health</span>
            <div className="flex items-center gap-2">
              <div className="w-20 h-2 bg-[#27272A] rounded-full overflow-hidden border border-[#3B82F6]/30">
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ 
                    width: `${healthScore}%`,
                    background: healthScore > 60 ? '#22C55E' : healthScore > 40 ? '#F97316' : '#EF4444'
                  }}
                />
              </div>
              <span className="text-white font-bold text-sm">{healthScore.toFixed(0)}%</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={pulseData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <XAxis 
              dataKey="date" 
              stroke="#52525B" 
              tick={{ fill: '#A1A1AA', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              yAxisId="left"
              stroke="#52525B" 
              tick={{ fill: '#A1A1AA', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(val) => `${(val / 1000).toFixed(1)}K`}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              stroke="#52525B" 
              tick={{ fill: '#A855F7', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(val) => `$${(33000 + (val / 100) * 5000) / 1000}K`}
              domain={[0, 100]}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine yAxisId="left" y={0} stroke="#27272A" />
            
            <Bar yAxisId="left" dataKey="added" fill="#22C55E" opacity={0.8} radius={[2, 2, 0, 0]}>
              {pulseData.map((entry, index) => (
                <Cell key={`added-${index}`} fill="#22C55E" />
              ))}
            </Bar>
            <Bar yAxisId="left" dataKey="sold" fill="#EF4444" opacity={0.8} radius={[2, 2, 0, 0]}>
              {pulseData.map((entry, index) => (
                <Cell key={`sold-${index}`} fill="#EF4444" />
              ))}
            </Bar>
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="priceNormalized" 
              stroke="#A855F7" 
              strokeWidth={3}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-[#27272A]">
        <div className="text-center">
          <p className="text-[#A1A1AA] text-xs mb-1">Total Added (14d)</p>
          <p className="text-xl font-bold text-[#22C55E]">
            {pulseData.reduce((acc, d) => acc + d.added, 0).toLocaleString()}
          </p>
        </div>
        <div className="text-center">
          <p className="text-[#A1A1AA] text-xs mb-1">Total Sold (14d)</p>
          <p className="text-xl font-bold text-[#EF4444]">
            {pulseData.reduce((acc, d) => acc + d.sold, 0).toLocaleString()}
          </p>
        </div>
        <div className="text-center">
          <p className="text-[#A1A1AA] text-xs mb-1">Net Inventory Change</p>
          <p className={`text-xl font-bold ${avgNetChange >= 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
            {avgNetChange >= 0 ? '+' : ''}{(avgNetChange * 14).toFixed(0).toLocaleString()}
          </p>
        </div>
        <div className="text-center">
          <p className="text-[#A1A1AA] text-xs mb-1">Avg Daily Turnover</p>
          <p className="text-xl font-bold text-white">
            {Math.round((pulseData.reduce((acc, d) => acc + d.added + d.sold, 0) / 14) / 2).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MarketPulse;
